import { useFlag } from '@reflag/react-sdk';
import * as React from 'react';

import { useHiveBranch, useHiveRevision } from '@gitbook/hive-react';
import {
    Heading,
    SegmentedControl,
    SidePanel,
    SidePanelBody,
    SidePanelBodyContent,
    SidePanelCloseButton,
    SidePanelHeader,
    SidePanelHeaderActions,
    SidePanelHeaderMain,
    SidePanelHeaderSecondary,
    SidePanelHeaderTitle,
} from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { useCurrentHivePage } from '../../../../Components';
import { useNavigation, useSetAnalyticsScreen } from '../../../../navigation';
import { useAdaptiveContextForSpace } from '../../../Site';
import { PageOptionsTabLayout } from './PageOptionsTabLayout';
import { PageOptionsTabMetadata } from './PageOptionsTabMetadata';
import { PageOptionsTabVisibility } from './PageOptionsTabVisibility';
import type { PageOptionsSidePanelProps } from './types';

/**
 * Side-panel to configure page options (layout, images, content, sidebars, footer)
 */
export function PageOptionsSidePanel(props: PageOptionsSidePanelProps): React.ReactElement {
    const { screen, space, analyticsContext } = props;
    const featurePageMetadata = useFlag('PAGE_META_DATA');

    const { navigate } = useNavigation();

    const branch = useHiveBranch();
    assertIsDefined(branch);
    const selectedTab = screen.sideSheet.tab ?? 'layout';

    const { revision } = useHiveRevision();

    const [page] = useCurrentHivePage(revision, screen.page);

    const adaptiveContentContext = useAdaptiveContextForSpace(space);

    const onClose = React.useCallback(
        (replace = false) => {
            navigate(
                {
                    current: {
                        sideSheet: undefined,
                    },
                },
                { replace }
            );
        },
        [navigate]
    );

    // If the page is not found, we close the side panel
    const pageIsMissing = !page;
    React.useEffect(() => {
        if (pageIsMissing) {
            onClose(true);
        }
    }, [pageIsMissing, onClose]);

    useSetAnalyticsScreen(
        page
            ? {
                  ...analyticsContext,
                  screen: 'space_page_options',
                  screenTab: selectedTab,
                  page: page.id,
              }
            : null,
        [analyticsContext, page?.id]
    );

    const tabOptions = [
        {
            key: 'layout',
            label: 'Layout',
        },
        {
            key: 'visibility',
            label: 'Visibility',
        },
    ] as const;

    return (
        <SidePanel onClose={() => onClose()}>
            <SidePanelHeader>
                <SidePanelHeaderMain>
                    <SidePanelHeaderTitle>
                        <Heading>Page options</Heading>
                    </SidePanelHeaderTitle>
                    <SidePanelHeaderActions>
                        <SidePanelCloseButton />
                    </SidePanelHeaderActions>
                </SidePanelHeaderMain>
                <SidePanelHeaderSecondary>
                    <SegmentedControl
                        options={[
                            ...tabOptions,
                            ...(featurePageMetadata.isEnabled
                                ? ([{ key: 'metadata', label: 'Metadata' }] as const)
                                : []),
                        ].map((tab) => ({
                            value: tab.key,
                            label: tab.label,
                        }))}
                        size="small"
                        active={selectedTab}
                        onChange={(tab) => {
                            navigate({
                                current: {
                                    sideSheet: {
                                        ...screen.sideSheet,
                                        tab,
                                    },
                                },
                            });
                        }}
                    />
                </SidePanelHeaderSecondary>
            </SidePanelHeader>
            <SidePanelBody>
                <SidePanelBodyContent>
                    {page && selectedTab === 'layout' ? (
                        <PageOptionsTabLayout
                            {...props}
                            branch={branch}
                            revision={revision}
                            page={page}
                            adaptiveContentContext={adaptiveContentContext}
                        />
                    ) : null}
                    {page && selectedTab === 'visibility' ? (
                        <PageOptionsTabVisibility
                            {...props}
                            branch={branch}
                            revision={revision}
                            page={page}
                            adaptiveContentContext={adaptiveContentContext}
                        />
                    ) : null}
                    {page && selectedTab === 'metadata' && featurePageMetadata.isEnabled ? (
                        <PageOptionsTabMetadata
                            {...props}
                            branch={branch}
                            revision={revision}
                            page={page}
                            adaptiveContentContext={adaptiveContentContext}
                        />
                    ) : null}
                </SidePanelBodyContent>
            </SidePanelBody>
        </SidePanel>
    );
}
